import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * UK percentage marks and degree classes → US letter grades and GPA.
 *
 * There is no official conversion; US universities, credential evaluators and UK universities
 * all publish their own. These are three published tables a reader can choose between, each
 * retrieved October 2026:
 *
 * - "andrews": Andrews University, International Student Services, "European Country
 *   Requirements", United Kingdom, undergraduate bachelor's degree classification (revised
 *   September 2022), https://www.andrews.edu/services/international/grcountryreqs/europe-gr.html —
 *   70–100% First, A, 4.00; 60–69% upper second, A−/B+, 3.50; 50–59% lower second, B, 3.00;
 *   40–49% third, C+, 2.33; 35–39% pass, C, 2.00; below that F. It prints GPA values, which
 *   are used as published.
 * - "ncsu": NC State University Study Abroad, "Europe Grade Equivalencies", United Kingdom
 *   (which calls itself "a guide rather than an absolute standard"):
 *   70–100 A; 65–69 A−; 60–64 B+; 50–59 B; 45–49 C+; 40–44 C; 0–39 F,
 *   https://studyabroad.ncsu.edu/europe-grade-equivalencies/. Letters only, so the points are
 *   the common US plus/minus values.
 * - "uconn": University of Connecticut, Experiential Global Learning, "Credit & grade
 *   conversion — Europe" (last updated January 2023), the Warwick table, which matches its
 *   Nottingham table: 70+ A; 65–69 A−; 60–64 B+; 55–59 B; 50–54 B−; 47–49 C+; 44–46 C; 40–43 C−;
 *   35–39 D+; below 35 F, https://experiential.global.uconn.edu/credit-grade-conversion/europe/.
 *   UConn publishes it for Warwick marks; it is used here as a finer-grained published scale.
 *   Plus/minus points are the common US values.
 *
 * The degree-class boundaries (First 70, upper second 60, lower second 50, third 40) are the
 * conventional ones the UK Degree Classification Calculator page documents from the UKSCQA
 * sector report of November 2018; individual universities add their own rounding and algorithm.
 */

export type UkUsScale = "andrews" | "ncsu" | "uconn";
export const UK_US_SCALES: readonly UkUsScale[] = ["andrews", "ncsu", "uconn"];

export interface UkUsBand {
  /** Lowest mark, inclusive. */
  min: number;
  letter: string;
  points: number;
}

const ANDREWS: readonly UkUsBand[] = [
  { min: 70, letter: "A", points: 4 },
  { min: 60, letter: "A−/B+", points: 3.5 },
  { min: 50, letter: "B", points: 3 },
  { min: 40, letter: "C+", points: 2.33 },
  { min: 35, letter: "C", points: 2 },
  { min: 0, letter: "F", points: 0 },
];

const NCSU: readonly UkUsBand[] = [
  { min: 70, letter: "A", points: 4 },
  { min: 65, letter: "A−", points: 3.7 },
  { min: 60, letter: "B+", points: 3.3 },
  { min: 50, letter: "B", points: 3 },
  { min: 45, letter: "C+", points: 2.3 },
  { min: 40, letter: "C", points: 2 },
  { min: 0, letter: "F", points: 0 },
];

const UCONN: readonly UkUsBand[] = [
  { min: 70, letter: "A", points: 4 },
  { min: 65, letter: "A−", points: 3.7 },
  { min: 60, letter: "B+", points: 3.3 },
  { min: 55, letter: "B", points: 3 },
  { min: 50, letter: "B−", points: 2.7 },
  { min: 47, letter: "C+", points: 2.3 },
  { min: 44, letter: "C", points: 2 },
  { min: 40, letter: "C−", points: 1.7 },
  { min: 35, letter: "D+", points: 1.3 },
  { min: 0, letter: "F", points: 0 },
];

export function ukUsBands(scale: UkUsScale): readonly UkUsBand[] {
  if (scale === "andrews") {
    return ANDREWS;
  }
  return scale === "ncsu" ? NCSU : UCONN;
}

export function isValidUkMark(mark: number): boolean {
  return Number.isFinite(mark) && mark >= 0 && mark <= 100;
}

export function convertUkMark(mark: number, scale: UkUsScale): UkUsBand | null {
  if (!isValidUkMark(mark)) {
    return null;
  }
  return ukUsBands(scale).find((band) => mark >= band.min) ?? null;
}

export type UkClass =
  | "first"
  | "upper-second"
  | "lower-second"
  | "third"
  | "below";

export const UK_CLASSES: readonly UkClass[] = [
  "first",
  "upper-second",
  "lower-second",
  "third",
];

/** Conventional degree-class boundaries on an average mark. */
export function ukClassOf(average: number): UkClass | null {
  if (!isValidUkMark(average)) {
    return null;
  }
  if (average >= 70) {
    return "first";
  }
  if (average >= 60) {
    return "upper-second";
  }
  if (average >= 50) {
    return "lower-second";
  }
  return average >= 40 ? "third" : "below";
}

/** The lowest and highest mark a class covers (a second's top is 69.99… handled as 69). */
const CLASS_MARKS: Record<Exclude<UkClass, "below">, [number, number]> = {
  first: [70, 100],
  "upper-second": [60, 69],
  "lower-second": [50, 59],
  third: [40, 49],
};

export interface ClassRange {
  lowest: UkUsBand;
  highest: UkUsBand;
}

/** The US bands at the bottom and top of a degree class on the chosen table. */
export function classRange(
  ukClass: Exclude<UkClass, "below">,
  scale: UkUsScale
): ClassRange | null {
  const [low, high] = CLASS_MARKS[ukClass];
  const lowest = convertUkMark(low, scale);
  const highest = convertUkMark(high, scale);
  return lowest && highest ? { lowest, highest } : null;
}

export interface UkModule {
  mark: number;
  credits: number;
}

export interface UkUsSummary {
  average: number;
  gpa: number;
  band: UkUsBand;
  ukClass: UkClass;
  totalCredits: number;
}

export function summariseUk(
  modules: readonly UkModule[],
  scale: UkUsScale
): UkUsSummary | null {
  let credits = 0;
  let markSum = 0;
  let pointSum = 0;
  for (const module of modules) {
    const band = convertUkMark(module.mark, scale);
    if (band && module.credits > 0) {
      credits += module.credits;
      markSum += module.mark * module.credits;
      pointSum += band.points * module.credits;
    }
  }
  if (credits <= 0) {
    return null;
  }
  const average = cleanFloat(markSum / credits);
  const band = convertUkMark(average, scale);
  const ukClass = ukClassOf(average);
  if (!(band && ukClass)) {
    return null;
  }
  return {
    average,
    gpa: cleanFloat(pointSum / credits),
    band,
    ukClass,
    totalCredits: credits,
  };
}
